import React, {
  useEffect,
  useState,
  useCallback,
  memo,
  RefObject,
  useRef,
} from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {Box, Typography} from '@mui/material';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import DragAndDropItems, {Items} from './DragAndDropItems';
import {t} from 'i18next';

type ID = string | number;

const SortableItem = memo(function SortableItem({
  item,
  allItems,
  handleRemoveClick,
  itemRef,
}: {
  item: Items;
  allItems: Items[];
  handleRemoveClick: (id: ID) => void;
  itemRef?: (node: HTMLDivElement | null) => void;
}) {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
    useSortable({
      id: item.id,
      data: item,
    });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        position: 'relative' as const,
        zIndex: isDragging ? 999 : 'auto',
        touchAction: 'none' as const,
      }
    : undefined;

  const handleProps = {...listeners, ...attributes};

  return (
    <div
      ref={(node) => {
        setNodeRef(node); // keep dnd-kit ref
        itemRef?.(node); // store DOM node for focus management
      }}
      tabIndex={-1}
      data-selected-item-id={String(item.id)}
      className={`sortable-item ${isDragging ? 'is-dragging' : ''}`}
      data-testid={`drag-and-drop-selected-list-div-${item.id}`}
    >
      <DragAndDropItems
        dargItems={item}
        dargAllItems={allItems}
        handleRemoveClick={() => handleRemoveClick(item.id)}
        style={style}
        dragHandleProps={handleProps}
      />
    </div>
  );
});

interface DragAndDropSelectedListProps {
  selectedItems: Items[];
  onReorder?: (newOrdered: Items[]) => void;
  onRemove?: (id: string) => void;
  selectedHeadingRef?: RefObject<HTMLHeadingElement | null>;
  maxColumnsRef?: RefObject<HTMLParagraphElement | null>;
}

const DragAndDropSelectedList: React.FC<DragAndDropSelectedListProps> = ({
  selectedItems,
  onReorder,
  onRemove,
  selectedHeadingRef,
  maxColumnsRef,
}) => {
  const [, setActiveId] = useState<ID | null>(null);
  const [items, setItems] = useState<Items[]>(selectedItems);

  const itemRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingFocusItemIdRef = useRef<string | null>(null);

  useEffect(() => {
    setItems(selectedItems);
  }, [selectedItems]);

  useEffect(() => {
    if (!pendingFocusItemIdRef.current) return;

    const nextFocusId = pendingFocusItemIdRef.current;
    pendingFocusItemIdRef.current = null;

    requestAnimationFrame(() => {
      const nextRow = itemRowRefs.current[nextFocusId];
      if (nextRow) {
        nextRow.focus();
      } else {
        selectedHeadingRef?.current?.focus();
      }
    });
  }, [items, selectedHeadingRef]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {distance: 1},
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as ID);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      try {
        const {active, over} = event;
        if (!over) {
          setActiveId(null);
          return;
        }

        if (active.id !== over.id) {
          const activeIdNum = active.id;
          const overIdNum = over.id;

          const oldIndex = items.findIndex((it) => it.id === activeIdNum);
          const newIndex = items.findIndex((it) => it.id === overIdNum);

          if (oldIndex !== -1 && newIndex !== -1) {
            const newItems = arrayMove(items, oldIndex, newIndex).map(
              (it, idx) => ({...it, sequence: idx + 1})
            );
            setItems(newItems);
            if (onReorder) onReorder(newItems);
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // swallow
      } finally {
        setActiveId(null);
      }
    },
    [items, onReorder]
  );

  const handleRemoveClick = useCallback(
    (id: ID) => {
      setItems((prev) => {
        const removedIndex = prev.findIndex((it) => it.id === id);
        const filtered = prev
          .filter((it) => it.id !== id)
          .map((it, idx) => ({...it, sequence: idx + 1}));

        const nextFocusItem =
          filtered[removedIndex] ?? filtered[removedIndex - 1] ?? null;

        pendingFocusItemIdRef.current = nextFocusItem
          ? String(nextFocusItem.id)
          : null;
        if (onReorder) onReorder(filtered);
        if (onRemove) onRemove(String(id));
        return filtered;
      });
    },
    [onReorder, onRemove]
  );

  const handleSelectedHeadingKeyDown = (
    e: React.KeyboardEvent<HTMLHeadingElement>
  ) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      maxColumnsRef?.current?.focus();
    }
  };

  return (
    <div
      className="dragdrop-list"
      data-testid={`drag-and-drop-selected-list-max-limit-wrapper`}
    >
      <div
        className="title-container d-flex display-between"
        data-testid={`drag-and-drop-selected-list-max-limit`}
      >
        <Typography
          ref={selectedHeadingRef}
          tabIndex={0}
          role="heading"
          aria-level={2}
          className="typo-text subhead4_size text box-sub-heading"
          onKeyDown={handleSelectedHeadingKeyDown}
          data-testid={`drag-and-drop-selected-list-typography`}
        >
          {`${t('docList.manageColumn.selected')} (${items?.length ?? 0})`}
        </Typography>
        <Typography
          ref={maxColumnsRef}
          tabIndex={0}
          className="typo-text body3_size text-color"
          data-testid={`drag-and-drop-selected-list-max-limit-text`}
        >
          {t('docList.manageColumn.maxLimit')}
        </Typography>
      </div>
      <Box className="editor-canvas arr-drawer__content scrollable">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext
            items={items.map((it) => it.id)}
            strategy={rectSortingStrategy}
            id="coding-form-items"
          >
            {items.map((item) => (
              <SortableItem
                key={String(item.id)}
                item={item}
                allItems={items}
                handleRemoveClick={handleRemoveClick}
                itemRef={(node) => {
                  itemRowRefs.current[String(item.id)] = node;
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>
    </div>
  );
};

export default DragAndDropSelectedList;
